// src/pages/MyDashboardPage.jsx
//
// Redesigned to match the AFSC reference's ProfilePage: an identity header
// (avatar initial + username + role badge) up top, a stat-card row for the
// throughput numbers, then a grid of small square-ish item cards (AFSC's
// TaskCard / .task-card-grid pattern -- auto-fill minmax(230px,1fr), so
// cards size themselves to roughly-square rather than stretching full
// width). Per Jerome: keep our color-coded status badges (status-pending/
// in_progress/submitted/approved/completed) rather than switching to
// AFSC's fixed badge-pending/claimed/reviewed/reported set -- those are
// two different status vocabularies and ours already carries the meaning.
//
// GET /api/tracker/dashboard/mine/ returns items grouped by status; this
// page flattens them back into one list (each item tagged with its own
// status) to render as individual cards, then also derives the per-status
// counts for the stat row from that same grouping.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/client";
import Navbar from "../components/Navbar";
import { ALL_STATUS_CHOICES, STAGE_TITLES, anyStatusLabel } from "../constants";

export default function MyDashboardPage() {
  const [user, setUser] = useState(null);
  const [grouped, setGrouped] = useState(null);
  const [projectIdByNo, setProjectIdByNo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      apiFetch("/tracker/dashboard/mine/"),
      apiFetch("/tracker/projects/"),
      apiFetch("/user/"),
    ])
      .then(([dashboard, projects, me]) => {
        if (cancelled) return;
        setGrouped(dashboard);
        setUser(me);
        const byNo = {};
        projects.forEach((p) => {
          byNo[p.project_no] = p.id;
        });
        setProjectIdByNo(byNo);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your dashboard.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const statusOrder = grouped
    ? [
        ...ALL_STATUS_CHOICES.map(([value]) => value).filter((v) => v in grouped),
        ...Object.keys(grouped).filter(
          (v) => !ALL_STATUS_CHOICES.some(([value]) => value === v)
        ),
      ]
    : [];

  const items = grouped
    ? statusOrder.flatMap((status) => grouped[status].map((item) => ({ ...item, status })))
    : [];

  return (
    <>
      <Navbar />
      <main className="page">
        {loading && <p>Loading...</p>}
        {error && <p className="form-error">{error}</p>}

        {!loading && !error && user && grouped && (
          <>
            <div className="card profile-identity-card">
              <div className="profile-identity">
                <div className="profile-avatar">
                  {(user.username || "?").slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div className="profile-name">{user.username}</div>
                  <div className="profile-sub">
                    <span className={`status-badge status-${user.role === "admin" ? "approved" : "in_progress"}`}>
                      {user.role || "analyst"}
                    </span>
                    {user.email && <span className="profile-sub-item">{user.email}</span>}
                  </div>
                </div>
              </div>
            </div>

            <h3>Your throughput</h3>
            <div className="stat-grid">
              {statusOrder.map((status) => (
                <div key={status} className="stat-card">
                  <span className="stat-value">{grouped[status].length}</span>
                  <span className={`stat-label status-badge status-${status}`}>
                    {anyStatusLabel(status)}
                  </span>
                </div>
              ))}
            </div>

            <h3>Your claimed items</h3>
            {items.length === 0 ? (
              <p className="stage-card-empty">You haven't claimed anything yet.</p>
            ) : (
              <div className="dashboard-item-grid">
                {items.map((item, i) => {
                  const projectId = projectIdByNo[item.project_no];
                  return (
                    <div key={`${item.project_no}-${item.stage}-${i}`} className="dashboard-item-card">
                      <div className="dashboard-item-head">
                        <span className="code">{item.project_no}</span>
                        <span className={`status-badge status-${item.status}`}>
                          {anyStatusLabel(item.status)}
                        </span>
                      </div>
                      <div className="dashboard-item-meta">
                        {STAGE_TITLES[item.stage] || item.stage}
                      </div>
                      <div className="dashboard-item-meta">
                        {item.status_changed_at
                          ? new Date(item.status_changed_at).toLocaleDateString()
                          : "--"}
                      </div>
                      {projectId && (
                        <div className="dashboard-item-actions">
                          <Link to={`/projects/${projectId}`}>View project</Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}
